<template>
  <div class="register-container">
    <Card class="register-card">
      <template #header>
        <div class="register-header">
          <h2 class="text-center mb-0">
            Register for AutoDenoise
          </h2>
        </div>
      </template>
      
      <template #content>
        <form @submit.prevent="handleRegister" class="register-form">
          <div class="form-group mb-3">
            <label for="username" class="form-label">Username</label>
            <input
              id="username"
              v-model="formData.username"
              type="text"
              class="form-control"
              placeholder="Choose a username (min 3 characters)"
              required
              :disabled="isLoading"
            />
            <small v-if="formData.username && formData.username.length < 3" class="text-danger">
              Username must be at least 3 characters long
            </small>
          </div>

          <div class="form-group mb-3">
            <label for="password" class="form-label">Password</label>
            <input
              id="password"
              v-model="formData.password"
              type="password"
              class="form-control"
              placeholder="Choose a password (min 6 characters)"
              required
              :disabled="isLoading"
            />
            <small v-if="formData.password && formData.password.length < 6" class="text-danger">
              Password must be at least 6 characters long
            </small>
          </div>

          <div class="form-group mb-3">
            <label for="confirmPassword" class="form-label">Confirm Password</label>
            <input
              id="confirmPassword"
              v-model="formData.confirmPassword"
              type="password"
              class="form-control"
              placeholder="Confirm your password"
              required
              :disabled="isLoading"
            />
            <small v-if="formData.confirmPassword && !passwordsMatch" class="text-danger">
              Passwords do not match
            </small>
          </div>

          <div class="form-group mb-3">
            <label for="inviteCode" class="form-label">Invite Code</label>
            <input
              id="inviteCode"
              v-model="formData.inviteCode"
              type="text"
              class="form-control"
              placeholder="Enter your invite code"
              required
              :disabled="isLoading"
            />
            <small class="text-muted">
              Registration requires a valid invite code
            </small>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mb-3">
            <i class="pi pi-exclamation-triangle me-2"></i>
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="alert alert-success mb-3">
            <i class="pi pi-check-circle me-2"></i>
            {{ successMessage }}
          </div>

          <Button
            type="submit"
            :loading="isLoading"
            :disabled="!isFormValid"
            label="Register"
            icon="pi pi-user-plus"
            class="w-100 mb-3"
            severity="primary"
          />

          <div class="text-center">
            <span class="text-muted">Already have an account? </span>
            <a href="#" @click.prevent="$emit('switch-to-login')" class="text-primary">
              Login here
            </a>
          </div>
        </form>
      </template>
    </Card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Register',
  emits: ['register-success', 'switch-to-login'],
  
  data() {
    return {
      formData: {
        username: '',
        password: '',
        confirmPassword: '',
        inviteCode: ''
      },
      isLoading: false,
      errorMessage: '',
      successMessage: ''
    }
  },

  computed: {
    passwordsMatch() {
      return this.formData.password === this.formData.confirmPassword
    },

    isFormValid() {
      return this.formData.username.trim().length >= 3 && 
             this.formData.password.length >= 6 &&
             this.passwordsMatch &&
             this.formData.inviteCode.trim().length > 0
    }
  },

  methods: {
    getApiBaseUrl() {
      // For production, use the deployed API endpoint with /api prefix
      if (process.env.NODE_ENV === 'production') {
        return `${window.location.protocol}//${window.location.hostname}/api`
      }

      // For development, always use localhost:3000 (backend server) with /api prefix
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const port = '3000'

      // If accessing via localhost, keep localhost (for development on same machine)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:${port}/api`
      }

      // For mobile or other devices, use the actual IP address with port 3000
      return `${protocol}//${hostname}:${port}/api`
    },

    async handleRegister() {
      if (!this.isFormValid) return

      this.isLoading = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const response = await axios.post(`${this.getApiBaseUrl()}/auth/register`, {
          username: this.formData.username.trim(),
          password: this.formData.password,
          inviteCode: this.formData.inviteCode.trim()
        })

        if (response.data.token) {
          this.successMessage = 'Registration successful! You are now logged in.'
          
          // Store token in localStorage
          localStorage.setItem('authToken', response.data.token)
          localStorage.setItem('user', JSON.stringify(response.data.user))
          
          // Wait a moment to show success message, then emit success
          setTimeout(() => {
            this.$emit('register-success', {
              token: response.data.token,
              user: response.data.user
            })
          }, 1500)
        }

      } catch (error) {
        console.error('Registration error:', error)
        
        if (error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error
        } else {
          this.errorMessage = 'Registration failed. Please check your connection and try again.'
        }
      } finally {
        this.isLoading = false
      }
    },

    clearForm() {
      this.formData.username = ''
      this.formData.password = ''
      this.formData.confirmPassword = ''
      this.formData.inviteCode = ''
      this.errorMessage = ''
      this.successMessage = ''
    }
  },

  mounted() {
    // Focus on username field when component mounts
    this.$nextTick(() => {
      const usernameInput = document.getElementById('username')
      if (usernameInput) {
        usernameInput.focus()
      }
    })
  }
}
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.register-card {
  width: 100%;
  max-width: 450px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.register-header {
  color: black;
  padding-top: 4rem;
  border-radius: 8px 8px 0 0;
  margin: -1.5rem -1.5rem 0 -1.5rem;
}

.register-form {
  padding: 1rem;
}

.form-label {
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.5rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.6;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid transparent;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
}

.text-muted {
  color: #6c757d;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .register-container {
    padding: 1rem;
  }
  
  .register-header {
    padding: 1.5rem;
  }
}
</style>
